import React, { useState, useEffect, useCallback } from 'react';
import { getPortfolioInsights } from '../../api/insightsApi';
import NewsCard from '../../components/NewsCard';
import Navbar from '../../components/Navbar';
import SentimentGauge from '../../components/SentimentGauge';
import LoadingLines from '../../components/LoadingLines';
import { useToast } from '../../components/Toast';
import { CLUSTER_ORDER, getCluster } from '../../utils/newsClusters';
import { InboxIcon } from '@heroicons/react/24/outline';

const Insights = () => {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [filterSymbol, setFilterSymbol] = useState('all');
  const [filterSentiment, setFilterSentiment] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterCredibility, setFilterCredibility] = useState('all');
  const { addToast } = useToast();

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getPortfolioInsights(days);
      setInsights(data);
      
      if (data.message && data.message.includes('No holdings')) {
        addToast('Please sync your holdings first', 'info');
      }
    } catch (err) {
      console.error('Error fetching insights:', err);
      
      if (err.response?.status === 401) {
        setError('Your session has expired or requires authentication.');
        addToast('Authentication required', 'error');
      } else {
        setError(err.response?.data?.detail || 'Failed to initialize AI core.');
        addToast('Data stream interrupted', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [days, addToast]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const getFilteredNews = () => {
    const newsSource = insights?.allNews || insights?.news || [];
    if (!newsSource.length) return [];
    
    let filtered = newsSource;
    if (filterSymbol !== 'all') {
      filtered = filtered.filter(article => article.symbol === filterSymbol);
    }
    if (filterSentiment !== 'all') {
      filtered = filtered.filter(article =>
        article.sentiment?.label === filterSentiment
      );
    }
    if (filterCategory !== 'all') {
      filtered = filtered.filter(article => article.category === filterCategory);
    }
    if (filterCredibility !== 'all') {
      filtered = filtered.filter(article => article.credibility?.tier === filterCredibility);
    }
    return filtered;
  };

  const filteredNews = getFilteredNews();
  const allNewsForSymbols = insights?.allNews || insights?.news || [];
  const symbols = allNewsForSymbols.length
    ? [...new Set(allNewsForSymbols.map(a => a.symbol))].sort()
    : [];

  if (loading && !insights) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen pt-28">
          <LoadingLines />
        </div>
      </>
    );
  }

  if (error && !insights) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen pt-28 px-4">
        <div className="w-full max-w-md bg-white/10 dark:bg-gray-900/30 backdrop-blur-2xl border border-white/20 rounded-3xl p-8 text-center shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-rose-500 to-orange-500"></div>
          <div className="text-6xl mb-6 opacity-80">⚠️</div>
          <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-rose-400 to-orange-300 mb-4">Neural Link Offline</h2>
          <p className="text-gray-300 mb-8">{error}</p>
          <div className="flex gap-4 justify-center">
            {error.includes('expired') || error.includes('Authentication') ? (
              <a href="/login" className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl text-white font-medium transition-all backdrop-blur-sm">Re-Authenticate</a>
            ) : (
              <button onClick={fetchInsights} className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 hover:opacity-90 rounded-xl text-white font-medium transition-all shadow-[0_0_20px_rgba(59,130,246,0.3)]">Restart Engine</button>
            )}
          </div>
        </div>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 relative">
      <Navbar />
      {/* Dynamic Background */}
      <div className="fixed inset-0 z-[-1] overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-cyan-500/10 rounded-full blur-[120px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-2 text-gray-900 dark:text-white flex items-center gap-3">
              Neural <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-cyan-400">Insights</span>
            </h1>
            <p className="text-gray-500 dark:text-gray-400 font-medium">
              Real-time vector sentiment mapping across {insights?.holdingsAnalyzed || 0} active assets
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={fetchInsights}
              disabled={loading}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border font-medium transition-all backdrop-blur-md
                ${loading 
                  ? 'bg-white/5 border-white/5 text-gray-500 cursor-not-allowed' 
                  : 'bg-white/10 border-white/20 text-gray-800 dark:text-white hover:bg-white/20 shadow-lg'
                }`}
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div> Syncing</>
              ) : (
                <>♻️ Refresh Data</>
              )}
            </button>
          </div>
        </div>

        {/* Top KPIs Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {[
            { label: 'Ingested Articles', value: insights?.totalArticles || 0, icon: '📰', color: 'text-blue-500' },
            { label: 'Monitored Assets', value: insights?.holdingsAnalyzed || 0, icon: '🎯', color: 'text-cyan-500' },
            { label: 'Data Window', value: insights?.dateRange || 'N/A', icon: '⏳', color: 'text-purple-500' },
            { label: 'Engine Status', value: 'Live', icon: '⚡', color: 'text-green-500' }
          ].map((stat, i) => (
            <div key={i} className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl border border-white/20 dark:border-white/5 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all group">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold tracking-wide text-gray-500 dark:text-gray-400 uppercase">{stat.label}</span>
                <span className="text-xl opacity-70 group-hover:opacity-100 transition-opacity drop-shadow-md">{stat.icon}</span>
              </div>
              <div className={`text-3xl font-bold text-gray-900 dark:text-white tracking-tight`}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>

        {/* Core Analysis Dashboard */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Main Sentiment Radial */}
          <div className="col-span-1 lg:col-span-1 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl border border-white/20 dark:border-white/5 rounded-3xl p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] hover:border-blue-500/30 transition-all flex flex-col justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Global Sentiment</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Aggregated emotional vector across all assets</p>
            </div>
            
            <div className="flex-1 flex flex-col items-center justify-center transform scale-110">
              <SentimentGauge sentiment={insights?.overallSentiment} size="lg" />
            </div>

            <div className="mt-8 grid grid-cols-3 gap-2 p-4 bg-gray-50/50 dark:bg-black/20 rounded-2xl border border-gray-100 dark:border-white/5 backdrop-blur-sm">
              <div className="text-center">
                <div className="text-2xl font-black bg-clip-text text-transparent bg-gradient-to-b from-green-400 to-green-600">{insights?.overallSentiment?.positive_count || 0}</div>
                <div className="text-[10px] uppercase font-bold text-gray-500 mt-1">Bullish</div>
              </div>
              <div className="text-center border-x border-gray-200 dark:border-white/10">
                <div className="text-2xl font-black bg-clip-text text-transparent bg-gradient-to-b from-gray-400 to-gray-500">{insights?.overallSentiment?.neutral_count || 0}</div>
                <div className="text-[10px] uppercase font-bold text-gray-500 mt-1">Neutral</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-black bg-clip-text text-transparent bg-gradient-to-b from-red-400 to-red-600">{insights?.overallSentiment?.negative_count || 0}</div>
                <div className="text-[10px] uppercase font-bold text-gray-500 mt-1">Bearish</div>
              </div>
            </div>
          </div>

          {/* AI Generator Output */}
          <div className="col-span-1 lg:col-span-2 relative">
            <div className="absolute -inset-0.5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-[2rem] blur opacity-20 dark:opacity-30"></div>
            <div className="relative h-full bg-white/60 dark:bg-[#0B0F19]/80 backdrop-blur-2xl border border-white/50 dark:border-white/10 rounded-3xl p-4 sm:p-8 flex flex-col shadow-xl">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-purple-500 flex items-center justify-center shadow-lg">
                    <span className="text-xl">✨</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">OpenAI Cortex Analysis</h2>
                    <p className="text-xs font-medium text-blue-500 tracking-wider">GENERATIVE RAG PIPELINE</p>
                  </div>
                </div>
                {insights?.aiInsights?.generated_at && (
                  <div className="hidden md:block text-xs font-mono text-gray-400 bg-gray-100 dark:bg-black/30 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10">
                    {new Date(insights.aiInsights.generated_at).toLocaleTimeString()}
                  </div>
                )}
              </div>
              
              <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                {insights?.aiInsights?.insights && insights.aiInsights.insights !== 'AI insights unavailable' ? (
                  <div className="prose prose-blue dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 font-medium leading-relaxed whitespace-pre-line text-[15px]">
                    {insights.aiInsights.insights}
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center opacity-50">
                    <div className="text-4xl mb-4">💤</div>
                    <p className="text-lg font-medium text-gray-500">Awaiting AI Synthesis Data...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Micro-Gauges Grid */}
        {insights?.stockSentiments && Object.keys(insights.stockSentiments).length > 0 && (
          <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl border border-white/20 dark:border-white/5 rounded-3xl p-7 shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-2 h-6 bg-blue-500 rounded-full"></span>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Asset Emotion Breakdown</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-8 gap-x-4">
              {Object.entries(insights.stockSentiments).map(([symbol, sentiment]) => (
                <div key={symbol} className="text-center group relative cursor-crosshair">
                  <div className="absolute inset-x-0 -inset-y-4 bg-gray-100/50 dark:bg-white/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm -z-10"></div>
                  <div className="font-bold text-sm text-gray-900 dark:text-gray-200 mb-3 tracking-wider">{symbol}</div>
                  <div className="flex justify-center transform group-hover:scale-105 transition-transform duration-300">
                    <SentimentGauge sentiment={sentiment} size="sm" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filtering Interface */}
        <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl border border-white/20 dark:border-white/5 rounded-2xl p-4 md:p-5 flex flex-wrap items-center justify-between gap-4 shadow-sm sticky top-24 z-20">
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <div className="relative group">
              <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="appearance-none pl-4 pr-10 py-2.5 bg-white/60 dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:text-white backdrop-blur-md outline-none cursor-pointer transition-all hover:bg-white dark:hover:bg-black">
                <option value={1}>Last 24 hours</option>
                <option value={3}>Last 3 days</option>
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover:text-blue-500">▼</div>
            </div>
            
            <div className="relative group">
              <select value={filterSymbol} onChange={(e) => setFilterSymbol(e.target.value)} className="appearance-none pl-4 pr-10 py-2.5 bg-white/60 dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:text-white backdrop-blur-md outline-none cursor-pointer transition-all hover:bg-white dark:hover:bg-black max-w-[180px] truncate">
                <option value="all">All Assets</option>
                {symbols.map(symbol => (
                  <option key={symbol} value={symbol}>{symbol} ({allNewsForSymbols.filter(a => a.symbol === symbol).length})</option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover:text-blue-500">▼</div>
            </div>

            <div className="relative group">
              <select value={filterSentiment} onChange={(e) => setFilterSentiment(e.target.value)} className="appearance-none pl-4 pr-10 py-2.5 bg-white/60 dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:text-white backdrop-blur-md outline-none cursor-pointer transition-all hover:bg-white dark:hover:bg-black">
                <option value="all">Any Impact</option>
                <option value="positive">Bullish Only</option>
                <option value="neutral">Neutral Only</option>
                <option value="negative">Bearish Only</option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover:text-blue-500">▼</div>
            </div>

            <div className="relative group">
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="appearance-none pl-4 pr-10 py-2.5 bg-white/60 dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:text-white backdrop-blur-md outline-none cursor-pointer transition-all hover:bg-white dark:hover:bg-black max-w-[200px] truncate">
                <option value="all">All Categories</option>
                {CLUSTER_ORDER.filter(key => (insights?.categoryCounts?.[key] || 0) > 0).map(key => (
                  <option key={key} value={key}>{getCluster(key).label} ({insights.categoryCounts[key]})</option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover:text-blue-500">▼</div>
            </div>

            <div className="relative group">
              <select value={filterCredibility} onChange={(e) => setFilterCredibility(e.target.value)} className="appearance-none pl-4 pr-10 py-2.5 bg-white/60 dark:bg-black/40 border border-gray-200 dark:border-white/10 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:text-white backdrop-blur-md outline-none cursor-pointer transition-all hover:bg-white dark:hover:bg-black">
                <option value="all">Any Credibility</option>
                <option value="genuine">Genuine</option>
                <option value="speculation">Speculation</option>
                <option value="opinion">Opinion</option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover:text-blue-500">▼</div>
            </div>
          </div>

          <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg text-sm font-bold tracking-wide border border-blue-100 dark:border-blue-800/50 w-full md:w-auto text-center">
            {filteredNews.length} Signals Validated
          </div>
        </div>

        {/* Global News Feed */}
        <div className="space-y-5 relative">
          {filteredNews.length === 0 ? (
            <div className="bg-white/40 dark:bg-[#0B0F19]/60 backdrop-blur-2xl border border-white/20 dark:border-white/5 rounded-3xl p-16 text-center transform hover:scale-[1.01] transition-transform shadow-xl">
              <InboxIcon className="w-16 h-16 mb-6 mx-auto text-gray-400 dark:text-gray-500" />
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Signal Silence</h3>
              <p className="text-gray-500 dark:text-gray-400 text-lg max-w-md mx-auto">The algorithmic sweep found zero articles matching your exact filter parameters.</p>
            </div>
          ) : (
            filteredNews.map((article, index) => (
              <div key={index} className="transform hover:-translate-y-1 transition-transform duration-300">
                <NewsCard
                  article={article}
                  showSymbol={true}
                  watchlist={(insights?.watchlistSymbols || []).includes(article.symbol)}
                />
              </div>
            ))
          )}
        </div>
      </div>
{/* Custom Styles */}
<style dangerouslySetInnerHTML={{__html: `
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.3);
  border-radius: 20px;
}
.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.1);
}
`}} />
    </div>
  );
};

export default Insights;
